import type {Agent} from "@atproto/api";
import {
  AtpClientError,
  type SyncMetadata,
  stripStudySessionMetadata,
} from "@/app/atproto";
import {IDS, type StudySession} from "@/shared/types";

/**
 * Records a completed study session.
 *
 * @param agent - The authenticated ATProto agent.
 * @param session - The session data (possibly including local sync metadata).
 * @returns The created record's URI, CID, and value.
 */
export async function createStudySession(
  agent: Agent,
  session: Omit<StudySession, "$type"> & SyncMetadata,
) {
  const cleanSession = stripStudySessionMetadata(session);

  const record = {
    ...cleanSession,
    $type: IDS.StudySession,
  };

  const result = await agent.com.atproto.repo.createRecord({
    repo: agent.did ?? "",
    collection: IDS.StudySession,
    record,
  });
  if (!result.success)
    throw new AtpClientError("study.session", "create", "error creating new record");

  const {uri, cid} = result.data;
  return {uri, cid, value: record};
}

/**
 * Lists past study sessions.
 *
 * @param agent - The authenticated ATProto agent.
 * @param params - Query parameters (repo, limit, cursor, reverse).
 * @returns A list of session records and a cursor.
 */
export async function listStudySessions(
  agent: Agent,
  params?: {
    repo?: string;
    limit?: number;
    cursor?: string;
    reverse?: boolean;
  },
) {
  const {data} = await agent.com.atproto.repo.listRecords({
    repo: params?.repo ?? agent.did ?? "",
    collection: IDS.StudySession,
    limit: params?.limit,
    cursor: params?.cursor,
    reverse: params?.reverse,
  });

  return {
    cursor: data.cursor,
    records: data.records.map(record => ({
      uri: record.uri,
      cid: record.cid,
      value: record.value as unknown as StudySession,
    })),
  };
}
